import { useTable } from 'react-table'
import styled from 'styled-components'
import { colors, fontWeights, breakpoints } from '@/styles'
import Skeleton from '@/components/skeleton'
import TableTitle from './table-title'

const Wrapper = styled.div`
  width: 100%;
`

const Table = styled.table`
  display: table;
  width: 100%;
  table-layout: fixed;
`

const TableHead = styled.thead`
  background: ${colors.grey96};
  border-bottom: 2px solid ${colors.grey88};
`

const TableBody = styled.tbody`
  ${props =>
    props.hideBackgroundColor
      ? `
        > tr {
          border-bottom: 1px solid ${colors.grey88};
        }`
      : `
        > :nth-child(even) {
          background: ${colors.grey96};
        }
    `}

  @media (max-width: ${breakpoints.tabletLandscape}) {
    > :nth-child(even) {
      background: ${colors.white};
    }

    > :not(:last-child) {
      border-bottom: 1px solid ${colors.grey88};
    }
  }
`

const TableHeaderRow = styled.tr`
  border-bottom: 1.5px solid rgba(0, 0, 0, 0.15);
`

const THead = styled.th`
  white-space: nowrap;
  padding: 5px 10px;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: auto 0;

  > :first-child {
    margin-right: 5px;
  }
`

const HeaderText = styled.p`
  font-weight: ${fontWeights.bold};
  font-size: 14px;
  line-height: 16px;
  text-align: left;
  max-width: 160px;
  color: ${colors.grey16};
  margin: 10px 0;
  word-wrap: break-word;
  white-space: pre-wrap;
`

const TableBodyRow = styled.tr`
  ${props => props.showBorder && `border-bottom: 1px solid ${colors.grey88}`};
`

const TableCell = styled.td`
  width: fit-content;
  padding: 10px;

  ${props => props.verticalAlign && `vertical-align: ${props.verticalAlign}`};
`

const TableFooter = styled.tfoot`
  width: 100%;
  background: ${colors.grey96};
  border-width: 3px 0px;
  border-style: solid;
  border-color: ${colors.grey88};
  margin-top: 10px;
`

const InformationTable = ({
  data,
  columns,
  title = '',
  hideHeader = false,
  showStatus = false,
  hideBackgroundColor = false,
  showFooter = false,
  verticalAlign,
  showBorder = false,
  isLoading = false
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, footerGroups, rows, prepareRow } =
    useTable({
      columns,
      data
    })

  return (
    <Wrapper>
      {(title || showStatus) && <TableTitle title={title} showStatus={showStatus} />}
      <Table {...getTableProps()} aria-label={title}>
        {!hideHeader && (
          <TableHead>
            {headerGroups.map((headerGroup, index) => {
              return (
                index === 0 && (
                  <TableHeaderRow {...headerGroup.getHeaderGroupProps()} key={index}>
                    {headerGroup.headers.map((column, columnIndex) => {
                      return (
                        <THead
                          {...column.getHeaderProps({ style: { width: column.width } })}
                          key={columnIndex}
                        >
                          <HeaderWrapper>
                            <HeaderText>{column.render('Header')}</HeaderText>
                          </HeaderWrapper>
                        </THead>
                      )
                    })}
                  </TableHeaderRow>
                )
              )
            })}
          </TableHead>
        )}

        <TableBody {...getTableBodyProps()} hideBackgroundColor={hideBackgroundColor}>
          {rows.map((row, index) => {
            prepareRow(row)

            return (
              <TableBodyRow {...row.getRowProps()} key={index} showBorder={showBorder}>
                {row.cells.map((cell, cellIndex) => {
                  return (
                    <TableCell
                      {...cell.getCellProps({ style: { width: cell.column.width } })}
                      key={cellIndex}
                      verticalAlign={verticalAlign}
                    >
                      {isLoading ? <Skeleton width="100%" height={40} /> : cell.render('Cell')}
                    </TableCell>
                  )
                })}
              </TableBodyRow>
            )
          })}
        </TableBody>
        {showFooter && (
          <>
            <tbody>
              <tr height="20px"></tr>
            </tbody>
            <TableFooter>
              {footerGroups.map(group => (
                <TableBodyRow {...group.getFooterGroupProps()}>
                  {group.headers.map(column => (
                    <TableCell {...column.getFooterProps()} verticalAlign={verticalAlign}>
                      {isLoading ? <Skeleton width="100%" height={40} /> : column.render('Footer')}
                    </TableCell>
                  ))}
                </TableBodyRow>
              ))}
            </TableFooter>
          </>
        )}
      </Table>
    </Wrapper>
  )
}

export default InformationTable
